import { isPortrait } from '@src/js/common/utils';
import { browser, getiOSVer } from '@src/slide/infrastructure/module/independent/agent/common';

const MIN_ZOOM = 1;
// iPad 放大系数调整为 1.5 倍，后续检测画布尺寸后动态调整系数
const MAX_ZOOM = browser.isIPad ? 1.5 : 2;
const DEFAULT_ZOOM = 1;

const TOUCH_END_DELAY = 800;
const TOUCH_DELAY = 200;

/**
 * 不依赖alloy finger的移动端缩放，适用于canvas版本
 */
export class PPTPlayerMobileZoom {
    public isMultiPointTouch: boolean;
    public playerZoomScale: number;
    public isZooming: boolean;
    private zoomScale: number;
    private cachedTouche1?;
    private cachedTouche2?;
    private moveWrapperDom;
    private zoomWrapperDom;
    private touchMoveTimeout;
    private isSingleTouchMove;
    private singleTouchMoveTimeout;
    private initScale: number;
    private canvasSize: any;
    private cachedScrollX: number;
    private cachedScrollY: number;
    private element: HTMLElement;
    private touchEndTimeout: any;
    private getCurrentScroll: any;
    private updateCurrentScroll: any;
    private zoomChangeCallbackList: Array<Function> = [];

    constructor(props) {
        this.getCurrentScroll = props.getCurrentScroll;
        this.updateCurrentScroll = props.updateCurrentScroll;

        this.resetScale();
    }

    /**
     * 单指滑动，用于阻塞缩放
     */
    public initTouchMoveEvents = (id) => {
        this.moveWrapperDom = document.querySelector(id);
        if (this.moveWrapperDom) {
            this.moveWrapperDom.addEventListener('touchstart', this.handleSingleTouchStart);
        }
    };

    /**
     * 缩放事件，用于缩放画布
     */
    public initZoomEvents = (id) => {
        this.zoomWrapperDom = document.querySelector(id);
        if (this.zoomWrapperDom) {
            this.zoomWrapperDom.addEventListener('touchstart', this.handleZoomTouchStart);
        }
    };

    public dispose = () => {
        if (this.zoomWrapperDom) {
            this.zoomWrapperDom.removeEventListener('touchstart', this.handleZoomTouchStart);
        }
        if (this.moveWrapperDom) {
            this.moveWrapperDom.removeEventListener('touchstart', this.handleSingleTouchStart);
        }
        this.zoomChangeCallbackList = [];
    };

    public resetScale = (): void => {
        this.initScale = MIN_ZOOM;
        this.zoomScale = MIN_ZOOM;
        this.playerZoomScale = DEFAULT_ZOOM;
    };

    public setCanvasSize = (canvasSize): void => {
        this.canvasSize = canvasSize;
    };

    public addZoomChangeCallback = (callback) => {
        this.zoomChangeCallbackList.push(callback);
    };

    /**
     * 画布缩放处理
     * @param e
     */
    private handleZoomTouchStart = (e) => {
        if (e?.touches?.length === 2) {
            e.preventDefault();
            e.stopPropagation();
            if (this.isSingleTouchMove) return;
            this.isMultiPointTouch = true;
            this.initScale = this.zoomScale;
            this.cachedTouche1 = e.touches[0];
            this.cachedTouche2 = e.touches[1];
            const { currentScrollX, currentScrollY } = this.getCurrentScroll();
            this.cachedScrollX = currentScrollX;
            this.cachedScrollY = currentScrollY;
            this.element = document.querySelector('.spectacle-deck') as HTMLDivElement;

            window.addEventListener('touchmove', this.handleZoomTouchMove);
            window.addEventListener('touchend', this.handleZoomTouchEnd);
            window.addEventListener('touchcancel', this.handleZoomTouchEnd);
        }
    };

    private handleZoomTouchMove = (e) => {
        if (e?.touches?.length === 2) {
            e.preventDefault();
            clearTimeout(this.touchMoveTimeout);
            this.isZooming = true;
            const [touch1, touch2] = e.touches;
            const originDistance = this.getTouchDistance(this.cachedTouche1, this.cachedTouche2);
            const changeDistance = this.getTouchDistance(touch1, touch2);
            let zoom = this.initScale * (changeDistance / originDistance);
            if (zoom < MIN_ZOOM) {
                zoom = MIN_ZOOM;
            } else if (zoom > MAX_ZOOM) {
                zoom = MAX_ZOOM;
            }

            this.zoomScale = zoom;
            const { translateX, translateY } = this.getTranslate();
            const { originX, originY } = this.getTransformOrigin();

            if (this.element) {
                this.element.style.transform = `translate(${translateX}px, ${translateY}px) scale(${zoom})`;
                this.element.style.transformOrigin = `${originX} ${originY}`;
            }

            if (this.playerZoomScale !== DEFAULT_ZOOM) {
                this.updateCurrentScroll(0, 0);
                this.playerZoomScale = DEFAULT_ZOOM;
                this.zoomChangeCallbackList.forEach((cb) => {
                    cb?.();
                });
            }

            // 兜底，避免个别机型在极端场景下不触发touchend/touchcancel
            this.touchMoveTimeout = setTimeout(() => {
                this.handleZoomTouchEnd();
            }, TOUCH_END_DELAY);
        }
    };

    private getTransformOrigin() {
        const originX = isPortrait() ? 'left' : 'center';
        const originY = isPortrait() ? 'center' : 'top';
        return { originX, originY };
    }

    private getTranslate() {
        const center = [(this.cachedTouche2.pageX + this.cachedTouche1.pageX) / 2,
            (this.cachedTouche2.pageY + this.cachedTouche1.pageY) / 2];
        let translateX = 0;
        let translateY = 0;
        if (isPortrait()) {
            const left = (-center[0] * (this.zoomScale - this.initScale)) + this.cachedScrollX;
            const minTranslateX = -this.canvasSize.width * (this.zoomScale - DEFAULT_ZOOM);
            translateX = Math.min(Math.max(left, minTranslateX), 0);
        } else {
            const top = (-center[1] * (this.zoomScale - this.initScale)) + this.cachedScrollY;
            const minTranslateY = -this.canvasSize.height * (this.zoomScale - DEFAULT_ZOOM);
            translateY = Math.min(Math.max(top, minTranslateY), 0);
        }

        return { translateX, translateY };
    }

    private handleZoomTouchEnd = (e?) => {
        e?.preventDefault();
        clearTimeout(this.touchMoveTimeout);

        if (this.touchEndTimeout) {
            clearTimeout(this.touchEndTimeout);
        }

        this.touchEndTimeout = setTimeout(() => {
            this.isMultiPointTouch = false;
        }, TOUCH_DELAY);
        if (!this.isZooming) {
            return;
        }
        this.isZooming = false;
        this.cachedTouche1 = undefined;
        this.cachedTouche2 = undefined;
        window.removeEventListener('touchmove', this.handleZoomTouchMove);
        window.removeEventListener('touchend', this.handleZoomTouchEnd);
        window.removeEventListener('touchcancel', this.handleZoomTouchEnd);
        this.playerZoomScale = this.zoomScale;

        const domRect = this.element?.getBoundingClientRect();
        const currentScrollX = Math.min(domRect?.left, 0);
        const currentScrollY = Math.min(domRect?.top, 0);
        this.updateCurrentScroll(currentScrollX, currentScrollY);
        this.zoomChangeCallbackList.forEach((cb) => {
            cb?.();
        });
        if (this.element) {
            this.element.style.transform = `scale(${1})`;
        }
    };

    private getTouchDistance = (touch1, touch2): number => {
        const x = touch1.pageX - touch2.pageX;
        const y = touch1.pageY - touch2.pageY;
        return Number(Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2)).toFixed(2));
    };

    /**
     * 触发单指滑动时，禁用缩放
     */
    private handleSingleTouchStart = () => {
        this.isSingleTouchMove = false;
        document.body.addEventListener('touchmove', this.handleSingleTouchMove, { passive: false });
        document.body.addEventListener('touchend', this.handleSingleTouchEnd);
        document.body.addEventListener('touchcancel', this.handleSingleTouchEnd);
    };

    private handleSingleTouchMove = () => {
        this.isSingleTouchMove = true;
        clearTimeout(this.singleTouchMoveTimeout);
        this.singleTouchMoveTimeout = setTimeout(() => {
            this.handleSingleTouchEnd();
        }, TOUCH_END_DELAY);
    };

    private handleSingleTouchEnd = () => {
        this.isSingleTouchMove = false;
        clearTimeout(this.singleTouchMoveTimeout);
        document.body.removeEventListener('touchmove', this.handleSingleTouchMove);
        document.body.removeEventListener('touchend', this.handleSingleTouchEnd);
        document.body.removeEventListener('touchcancel', this.handleSingleTouchEnd);
    };
}
